import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { router } from './router';
import ErrorBoundary from './ErrorBoundary';
import './index.css';
import { useEffect, useRef } from 'react';
import { supabase } from './utils/supabaseClient';
import {
  WORK_EMAIL_REQUIRED_MESSAGE,
  exchangeSessionCookieDetailed,
  isWorkEmailRequiredExchange,
  setCachedAccessToken,
} from './utils/api';
import { apiFetch } from './utils/api';
import { useAuth } from './stores/authStore';
import CookieBanner from './components/CookieBanner';
import PublicLiveChat from './components/PublicLiveChat';
import { SystemStatusBanner } from './components/SystemStatusBanner';
import { prepareFreshTrialLogin } from './lib/freshTrialLogin';
import { peekPendingInviteToken } from './lib/pendingInvite';
import { captureSignupUtmFromLocation, pingLifecycle } from './lib/signupUtm';
import { oauthAvatarUrl } from './lib/oauthAvatarUrl';
import { useToastPlacement } from './stores/toastPlacementStore';

const AUTH_ENABLED = import.meta.env.VITE_AUTH_ENABLED !== 'false';

function App() {
  const { user, login, logout } = useAuth();
  const trialGrantedRef = useRef(false);
  // Bottom right, unless the open screen moves toasts off its main button.
  const toastPosition = useToastPlacement((state) => state.position);

  const redirectToAuthWithError = (message: string) => {
    const authIntent = window.sessionStorage.getItem('halr_auth_intent');
    window.sessionStorage.removeItem('halr_auth_intent');
    const targetPath = authIntent === 'signup' ? '/signup' : '/login';
    const url = new URL(targetPath, window.location.origin);
    url.searchParams.set('auth_error', 'work_email_required');
    url.searchParams.set('message', message);
    window.location.replace(url.toString());
  };

  const handleSession = async (session: any, grantTrial: boolean) => {
    setCachedAccessToken(session.access_token || null);
    const exchange = await exchangeSessionCookieDetailed();
    if (!exchange.ok) {
      // Only tear down shared auth state on a definitive policy rejection
      // (work-email required). For transient/network exchange failures, leave
      // the cached token and Supabase session intact — clearing them here can
      // race a concurrent exchange from Login/SignUp/ProtectedRoute and log the
      // user out of an otherwise-valid session.
      if (isWorkEmailRequiredExchange(exchange)) {
        setCachedAccessToken(null);
        await supabase?.auth.signOut().catch(() => {});
        logout();
        redirectToAuthWithError(exchange.message || WORK_EMAIL_REQUIRED_MESSAGE);
      }
      return;
    }
    const sessionUser = session.user as any;
    const meta: any = sessionUser.user_metadata || {};
    const name = meta.full_name || meta.name || '';
    const avatarUrl = oauthAvatarUrl(sessionUser);
    login({
      id: session.user.id,
      email: session.user.email,
      name,
      avatar_url: avatarUrl,
      avatarUrl,
      user_metadata: meta,
      identities: sessionUser.identities,
    });
    // Supabase also reports SIGNED_IN when a stored session is restored on reload.
    const grantedKey = `halr_trial_granted:${session.user.id}`;
    if (grantTrial && !trialGrantedRef.current && window.sessionStorage.getItem(grantedKey) !== 'true') {
      trialGrantedRef.current = true;
      window.sessionStorage.setItem(grantedKey, 'true');
      // Trial setup and the lifecycle ping are not needed to draw the first screen.
      void (async () => {
        try {
          if (!peekPendingInviteToken()) {
            await apiFetch('/api/subscription/start-trial', { method: 'POST' });
          }
        } catch {
          /* trial can be reconciled after paint */
        }
        await pingLifecycle(apiFetch, 'first_login');
        await prepareFreshTrialLogin();
      })();
    }
  };

  useEffect(() => {
    captureSignupUtmFromLocation();
    if (!AUTH_ENABLED || !supabase) return;

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        const sess = data?.session;
        if (sess?.user) {
          // Session recovery on reload — do not re-provision trial (would reset expired state).
          await handleSession(sess, false);
        }
      } catch (err) {
        console.error('[AUTH][BOOT] Error during boot probe:', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!AUTH_ENABLED || !supabase) return;

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        // Only provision trial on an explicit sign-in, not INITIAL_SESSION / token refresh.
        const grantTrial = event === 'SIGNED_IN';
        await handleSession(session, grantTrial);
      } else {
        setCachedAccessToken(null);
        logout();
      }
    });
    return () => { sub.subscription?.unsubscribe?.(); };
  }, []);

  return (
    <ErrorBoundary>
      <>
        <SystemStatusBanner />
        <RouterProvider router={router} />
        <PublicLiveChat />
        <Toaster
          position={toastPosition}
          toastOptions={{
            duration: 4000,
            style: {
              background: '#202020',
              color: '#ffffff',
              fontSize: '13px',
              borderRadius: '10px',
              padding: '10px 14px',
            },
            success: { iconTheme: { primary: '#ffffff', secondary: '#202020' } },
            error: { iconTheme: { primary: '#ef4444', secondary: '#ffffff' } },
          }}
        />
        <CookieBanner />
      </>
    </ErrorBoundary>
  );
}

export default App;
